import { useCallback, useEffect, useState } from 'react';
import { Check, Plus, Ruler } from 'lucide-react';
import type { UnidadMedidaVista } from '@shared/types';
import { NumberField } from '@renderer/components/ui/NumberField';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 63 (Fase 4) — Unidades de medida.
 *
 * Nace de una foto con un "?" dibujado sobre el desplegable del formulario de insumo:
 * *"¿dónde doy de alta o modifico UNIDAD DE MEDIDA?"*. La respuesta era "en ningún lado":
 * eran tres valores fijos en el código, y un negocio que compra por bolsa, caja o manojo
 * capturaba con la unidad equivocada.
 *
 * Dos decisiones de diseño:
 *
 *  1. **Las más comunes vienen listas, apagadas.** Bolsa, caja, botella, manojo, gramos…
 *     se activan con un clic en lugar de teclearse. El desplegable del insumo no crece
 *     con lo que este negocio no usa, pero tampoco hay que inventar nada.
 *  2. **Lo que está en uso no se puede apagar**, y se dice cuántos ingredientes lo usan.
 *     Apagar la unidad de un ingrediente activo dejaría el inventario hablando de algo
 *     que el sistema ya no reconoce.
 */

export function UnidadesMedidaSection() {
  const [unidades, setUnidades] = useState<UnidadMedidaVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nueva, setNueva] = useState({ nombre: '', descripcion: '', decimales: 0, fraccion: 1 });
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    const res = await window.api.unidades.listarTodas();
    if (res.ok) setUnidades(res.data);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const alternar = async (u: UnidadMedidaVista) => {
    setError(null);
    const res = await window.api.unidades.actualizar(u.id, { activo: !u.activo });
    if (res.ok) void cargar();
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const crear = async () => {
    setError(null);
    if (nueva.nombre.trim().length < 2) {
      setError('Escribe el nombre de la unidad, por ejemplo "Bolsas".');
      return;
    }
    setCreando(true);
    const res = await window.api.unidades.crear({
      // El código se deriva del nombre: una cosa menos que capturar, y el servidor lo
      // normaliza igual (minúsculas, sin acentos).
      codigo: nueva.nombre,
      nombre: nueva.nombre.trim(),
      descripcion: nueva.descripcion.trim() || undefined,
      decimales: nueva.decimales,
      fraccionSugerida: nueva.fraccion > 0 ? nueva.fraccion : null,
    });
    setCreando(false);
    if (res.ok) {
      setNueva({ nombre: '', descripcion: '', decimales: 0, fraccion: 1 });
      void cargar();
    } else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const activas = unidades.filter((u) => u.activo);
  const apagadas = unidades.filter((u) => !u.activo);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Ruler className="h-5 w-5 text-slate-400" />
          Unidades de medida
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Cómo compras cada ingrediente. Las que estén activas aparecen al crear o editar un
          ingrediente.
        </p>
      </div>

      {error && <div className="error-text">{error}</div>}

      {cargando ? (
        <p className="text-sm text-slate-400">Cargando…</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">Unidad</th>
                  <th className="px-4 py-2">Se usa en</th>
                  <th className="px-4 py-2 text-center">Activa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activas.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{u.nombre}</div>
                      {u.descripcion && (
                        <div className="text-xs text-slate-500">{u.descripcion}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {u.enUso && u.enUso > 0
                        ? `${u.enUso} ${u.enUso === 1 ? 'ingrediente' : 'ingredientes'}`
                        : 'nadie todavía'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.esSistema ? (
                        <span
                          className="text-xs text-slate-400"
                          title="Es una unidad base del sistema"
                        >
                          siempre
                        </span>
                      ) : (
                        <button
                          onClick={() => void alternar(u)}
                          className="min-h-[44px] px-3 text-sm font-medium text-slate-600 hover:text-red-700"
                        >
                          Desactivar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {apagadas.length > 0 && (
            <div>
              <p className="text-sm font-medium text-slate-700">
                Otras que puedes activar con un clic
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {apagadas.map((u) => (
                  <button
                    key={u.id}
                    onClick={() => void alternar(u)}
                    className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-slate-300 px-3 text-sm hover:border-primary hover:bg-primary/5"
                    title={u.descripcion ?? undefined}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {u.nombre}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-700">¿Falta alguna? Agrégala</p>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="label" htmlFor="uni-nombre">
                  Cómo se llama
                </label>
                <input
                  id="uni-nombre"
                  className="input"
                  placeholder="Ej: Bolsas, Cajas, Rollos"
                  value={nueva.nombre}
                  onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="uni-desc">
                  Para qué sirve (opcional)
                </label>
                <input
                  id="uni-desc"
                  className="input"
                  placeholder="Ej: se compra por bolsa cerrada"
                  value={nueva.descripcion}
                  onChange={(e) => setNueva({ ...nueva, descripcion: e.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="uni-dec">
                  Decimales al mostrar
                </label>
                <NumberField
                  id="uni-dec"
                  min={0}
                  max={3}
                  decimals={0}
                  className="input text-right font-mono"
                  value={nueva.decimales}
                  onChange={(n) => setNueva({ ...nueva, decimales: n })}
                />
                <p className="help-text">0 en lo que se cuenta: “30 bolsas”, no “30.000”.</p>
              </div>
              <div>
                <label className="label" htmlFor="uni-frac">
                  ¿Se puede partir?
                </label>
                <select
                  id="uni-frac"
                  className="input"
                  value={String(nueva.fraccion)}
                  onChange={(e) => setNueva({ ...nueva, fraccion: Number(e.target.value) })}
                >
                  <option value="1">No, se usa completa</option>
                  <option value="0.5">Sí, en mitades</option>
                  <option value="0.25">Sí, en cuartos</option>
                  <option value="0">Cualquier cantidad (se pesa o se mide)</option>
                </select>
                <p className="help-text">Es lo que se propone al crear un ingrediente.</p>
              </div>
            </div>
            <button
              onClick={() => void crear()}
              disabled={creando}
              className="btn-primary mt-3 inline-flex min-h-[44px] items-center gap-2"
            >
              <Check className="h-4 w-4" />
              {creando ? 'Agregando…' : 'Agregar unidad'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
